import * as React from 'react';
import { Translate } from 'react-redux-i18n';
import { ThemeProvider } from 'styled-components';

import {
	Overlay,
	OverlayBlur,
	PopupContainer,
	PopupHeader,
	PopupContentWrapper,
	PopupBody,
	Label,
	ButtonCancel,
	ButtonConfirm,
	PopupFooter,
	ButtonAccept,
	Loader,
} from './styles';
import keys from '../../utils/keys';
import Utils from '../../utils';

export enum PopupTheme {
	DARK = 'DARK',
	LIGHT = 'LIGHT',
}

interface FooterData {
	label: string;
	onAccept: () => void;
}
interface PopupProps {
	isDisplayed: boolean;
	label: string;
	onCancel?: () => void;
	onConfirm?: () => void;
	isConfirmDisabled?: boolean;
	footer?: FooterData;
	actionButtonColor?: string;
	isLoading?: boolean;
	className?: string;
	gallerySlot?: string;
	theme?: PopupTheme;
}

const Popup: React.FunctionComponent<PopupProps> = ({
	isDisplayed,
	label,
	onCancel,
	onConfirm,
	isConfirmDisabled,
	children,
	footer,
	actionButtonColor,
	isLoading,
	className,
	gallerySlot,
	theme = PopupTheme.LIGHT,
}) => {
	const handleOverlayClick = (e: MouseEvent | TouchEvent) => {
		if (onCancel && e.target === e.currentTarget) {
			onCancel();
		}
	};

	React.useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (keys.isEnter(e.key) && onConfirm && !isConfirmDisabled) {
				e.preventDefault();
				onConfirm();
			}

			if (keys.isEsc(e.key) && onCancel) {
				e.preventDefault();
				onCancel();
			}
		};

		if (isDisplayed) {
			window.addEventListener('keydown', handleKeyDown);
		}

		return () => {
			if (isDisplayed) {
				window.removeEventListener('keydown', handleKeyDown);
			}
		};
	}, [isDisplayed, onConfirm, onCancel, isConfirmDisabled]);

	return (
		<ThemeProvider theme={{ theme }}>
			<Overlay
				isOpen={isDisplayed}
				className={className}
				data-gallery-slot={gallerySlot}
			>
				<OverlayBlur
					onMouseDown={handleOverlayClick}
					onTouchStart={handleOverlayClick}
				>
					{isDisplayed && (
						<PopupContainer>
							<PopupHeader>
								{onCancel &&
									<ButtonCancel
										type="button"
										color={actionButtonColor}
										onClick={onCancel}
									>
										<Translate value="messages.cancel" />
									</ButtonCancel>
								}
								<Label>{label}</Label>
								{onConfirm &&
									<ButtonConfirm
										type="button"
										color={actionButtonColor}
										onClick={onConfirm}
										disabled={isConfirmDisabled}
									>
										<Translate value="messages.ok" />
									</ButtonConfirm>
								}
							</PopupHeader>
							<PopupContentWrapper>
								{isLoading &&
									<Loader>
										{Utils.getSvgIcon('iconset_loading')}
										<Translate value="messages.loading" />
									</Loader>
								}
								<PopupBody>
									{children}
								</PopupBody>
								{footer &&
									<PopupFooter>
										<ButtonAccept
											type="button"
											onClick={footer.onAccept}
										>
											{footer.label}
										</ButtonAccept>
									</PopupFooter>
								}
							</PopupContentWrapper>
						</PopupContainer>
					)}
				</OverlayBlur>
			</Overlay>
		</ThemeProvider>
	);
};

export { FooterData };
export default Popup;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Popup/index.tsx